import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  Check,
  ExternalLink,
  FileText,
  Loader2,
  Mail,
  MailOpen,
  MessageSquare,
  MoreHorizontal,
  MousePointerClick,
  Pause,
  Play,
  RefreshCcw,
  Send,
  Star,
  Trash2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatDateTime, initials } from "@/lib/formatters";
import {
  enrollCompany,
  pauseOutreach,
  removeOutreach,
} from "@/api/outreach";
import { usePermissions } from "@/hooks/usePermissions";
import { EmailDetailSheet } from "@/components/activity/EmailDetailSheet";
import { EnrollDialog } from "./EnrollDialog";
import { getCompanyEmail } from "@/api/email";

export type OutreachLoadStatus = "idle" | "loading" | "ok" | "error";
import type {
  CompanyDetail as CompanyDetailType,
  EmbeddedContact,
  OutreachBodyType,
  OutreachEvent,
  OutreachStatus,
} from "@/api/types";

interface OutreachSectionProps {
  projectSlug: string;
  company: CompanyDetailType;
  /** Outreach state owned by the page so the Lemlist fetch can fire in parallel
   *  with getCompany. The section is purely presentational w.r.t. this. */
  outreach: OutreachStatus | null;
  outreachStatus: OutreachLoadStatus;
  onOutreachData: (next: OutreachStatus | null) => void;
  onOutreachRefetch: () => Promise<void>;
  /** Lightweight: fired after every outreach action so the activity timeline
   *  can refetch and surface the new email_sequence_* log entry. */
  onActivityRefresh?: () => void;
  /** Heavy: fired only after Remove, since the backend clears the
   *  lemlist_* metadata keys on the company record and we need the new
   *  shape for the empty-state's "Previously enrolled in …" hint. */
  onCompanyReload?: () => void;
}

export function OutreachSection({
  projectSlug,
  company,
  outreach: data,
  outreachStatus,
  onOutreachData,
  onOutreachRefetch,
  onActivityRefresh,
  onCompanyReload,
}: OutreachSectionProps) {
  const perms = usePermissions();
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<OutreachEvent | null>(null);
  // One company, one way of being emailed: when an Email campaign holds one of
  // its contacts, this legacy panel does not offer to start a second sequence.
  const [heldBy, setHeldBy] = useState<string | null>(null);
  const notEnrolled = outreachStatus !== "loading" && !data?.is_in_campaign;
  useEffect(() => {
    if (!notEnrolled || !perms.canEdit || !perms.canViewEmail) return;
    let cancelled = false;
    getCompanyEmail(projectSlug, company.id)
      .then((email) => {
        const live = email.memberships.find((m) =>
          ["EMAIL_READY", "EMAIL_QUEUED", "EMAIL_ACTIVE", "EMAIL_PAUSED"].includes(m.status),
        );
        if (!cancelled) setHeldBy(live ? live.campaign_name : null);
      })
      .catch(() => !cancelled && setHeldBy(null));
    return () => {
      cancelled = true;
    };
  }, [projectSlug, company.id, notEnrolled, perms.canEdit, perms.canViewEmail]);

  const lemlistLeadUrl =
    typeof company.metadata?.lemlist_lead_url === "string"
      ? (company.metadata.lemlist_lead_url as string)
      : null;

  // Resume needs the contact_id — Lemlist's POST /enroll on a paused lead
  // unpauses it. We resolve the contact via the email the backend recorded,
  // since OutreachStatus only carries contact_email/contact_name.
  const resumeContact = data?.contact_email
    ? company.contacts.find((c) => c.email === data.contact_email) ?? null
    : null;
  const canResume = !!data?.campaign_id && !!resumeContact;

  async function pauseSequence() {
    if (pausing || !data) return;
    setPausing(true);
    // Optimistic: flip is_paused immediately so the badge + button swap
    // without waiting for the round-trip.
    const previous = data;
    onOutreachData({ ...data, is_paused: true, status: "paused" });
    try {
      await pauseOutreach(projectSlug, company.id);
      toast.success("Sequence paused");
      // Background reconciliation — confirms canonical state and surfaces
      // the new email_sequence_paused activity log.
      void onOutreachRefetch();
      onActivityRefresh?.();
    } catch (err) {
      onOutreachData(previous); // rollback
      toast.error(extractError(err));
    } finally {
      setPausing(false);
    }
  }

  async function resumeSequence() {
    if (resuming || !data || !data.campaign_id || !resumeContact) return;
    setResuming(true);
    const previous = data;
    onOutreachData({ ...data, is_paused: false, status: "active" });
    try {
      // POST /enroll on a paused lead unpauses it server-side.
      const next = await enrollCompany(projectSlug, company.id, {
        campaign_id: data.campaign_id,
        contact_id: resumeContact.id,
      });
      onOutreachData(next);
      toast.success("Sequence resumed");
      onActivityRefresh?.();
    } catch (err) {
      onOutreachData(previous); // rollback
      toast.error(extractError(err));
    } finally {
      setResuming(false);
    }
  }

  async function confirmRemove() {
    if (removing) return;
    setRemoving(true);
    const previous = data;
    // Optimistic: clear the section so the empty state shows immediately.
    onOutreachData({
      is_in_campaign: false,
      is_paused: false,
      events: [],
      stats: null,
    });
    try {
      await removeOutreach(projectSlug, company.id);
      setRemoveConfirmOpen(false);
      toast.success(
        previous?.previous_team
          ? "Removed in Prism. The lead stays in the previous Lemlist team."
          : "Lead removed from campaign",
      );
      onActivityRefresh?.();
      // Backend cleared the lemlist_* metadata keys — reload the company
      // so the empty state's "Previously enrolled in …" hint reflects the
      // new shape.
      onCompanyReload?.();
    } catch (err) {
      onOutreachData(previous ?? null); // rollback
      toast.error(extractError(err));
    } finally {
      setRemoving(false);
    }
  }

  function handleEnrolled(next: OutreachStatus) {
    onOutreachData(next);
    setEnrollOpen(false);
    toast.success(`Outreach started for ${company.name}`);
    onActivityRefresh?.();
  }

  // Show a skeleton only on the *initial* load. On subsequent refetches we
  // keep the previous data visible so the UI doesn't flash empty.
  if (outreachStatus === "loading" && data === null) {
    return (
      <Card>
        <div className="h-24 animate-pulse rounded-lg bg-slate-100" />
      </Card>
    );
  }

  // Outreach failed and we have no cached data — surface the failure with a
  // retry button. The rest of the page is intact.
  if (outreachStatus === "error" && data === null) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <AlertCircle className="h-8 w-8 text-slate-300" />
          <p className="text-sm text-slate-500">
            Unable to load outreach data
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void onOutreachRefetch()}
          >
            <RefreshCcw className="mr-2 h-3.5 w-3.5" />
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  const enrolled = !!data?.is_in_campaign;
  const hasContactWithEmail = company.contacts.some((c) => !!c.email);

  if (!enrolled) {
    const meta = (company.metadata ?? {}) as Record<string, unknown>;
    const isInEmailFlow = company.pipeline_status?.startsWith("email_");
    // `lemlist_lead_id` is the canonical per-lead identifier — present
    // for every enrolled prospect, even when the campaign_id wasn't
    // carried over by the migration. `lemlist_campaign_id` kept as a
    // fallback for older snapshots that only stored the campaign side.
    const hasLemlistEnrollment =
      typeof meta.lemlist_lead_id === "string" ||
      typeof meta.lemlist_campaign_id === "string";
    const hasMetadataTimeline =
      !!readMetaString(meta, "email_sent_at") ||
      !!readMetaString(meta, "email_opened_at") ||
      !!readMetaString(meta, "email_clicked_at") ||
      !!readMetaString(meta, "email_replied_at") ||
      meta.email_bounced === true ||
      typeof meta.emails_sent_count === "number" ||
      hasLemlistEnrollment;

    // Migrated leads: pipeline_status is in the email_* family but
    // Lemlist's /outreach endpoint returned is_in_campaign=false (no
    // live campaign tracked — campaign_id wasn't carried over by the
    // migration). Render whatever the metadata snapshot has so the
    // section doesn't lie with "No email sequence active".
    if (isInEmailFlow || hasMetadataTimeline) {
      return (
        <Card>
          <MigratedSequenceCard
            pipelineStatus={company.pipeline_status}
            meta={meta}
            lemlistLeadUrl={lemlistLeadUrl}
          />
        </Card>
      );
    }

    const previousCampaignName =
      typeof company.metadata?.lemlist_previous_campaign_name === "string"
        ? (company.metadata.lemlist_previous_campaign_name as string)
        : null;
    return (
      <>
        <Card>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-slate-900">Email sequence</h3>
            <LegacyTag />
          </div>
          <div className="flex flex-col items-center gap-3 py-6">
            <Mail className="h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-500">No email sequence active</p>
            {previousCampaignName && (
              <p className="text-xs text-slate-400">
                Previously enrolled in {previousCampaignName}
              </p>
            )}
            {perms.canEdit && hasContactWithEmail && heldBy && (
              <p className="max-w-sm text-center text-xs leading-relaxed text-slate-500">
                A contact of this company is in the Email campaign “{heldBy}”. A company is
                emailed one way only: no legacy sequence while it is there.
              </p>
            )}
            {perms.canEdit && hasContactWithEmail && !heldBy && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => setEnrollOpen(true)}
              >
                <Send className="mr-2 h-3.5 w-3.5" />
                Start outreach
              </Button>
            )}
            {perms.canEdit && !hasContactWithEmail && (
              <p className="text-xs text-slate-400">
                {company.contacts.length > 0
                  ? `Add an email to ${
                      company.contacts[0].full_name ?? "the contact"
                    } to start outreach.`
                  : "Add a contact with an email first."}
              </p>
            )}
          </div>
        </Card>
        <EnrollDialog
          open={enrollOpen}
          onOpenChange={setEnrollOpen}
          projectSlug={projectSlug}
          company={company}
          onEnrolled={handleEnrolled}
        />
      </>
    );
  }

  return (
    <>
      <Card>
        <SequenceHeader
          isPaused={!!data?.is_paused}
          previousTeam={!!data?.previous_team}
          campaignName={data?.campaign_name ?? null}
          canEdit={perms.canEdit}
          canResume={canResume}
          pausing={pausing}
          resuming={resuming}
          onPause={pauseSequence}
          onResume={resumeSequence}
          onAskRemove={() => setRemoveConfirmOpen(true)}
          // The previous team's app link opens nothing in the current team.
          lemlistLeadUrl={data?.previous_team ? null : lemlistLeadUrl}
        />

        {data?.is_paused && (
          <p className="mt-3 text-[13px] text-amber-700">
            Email sequence is paused
          </p>
        )}

        {data?.previous_team && (
          <p className="mt-3 text-[13px] text-slate-500">
            Previous Lemlist team: this history stays in Prism, read-only. Nothing is sent to Lemlist for this lead.
            {perms.canEdit &&
              " Remove from campaign ends it in Prism only: its contacts can then join an Email campaign."}
          </p>
        )}

        <div className="mt-4">
          <SequenceProgress
            current={data?.current_step ?? 1}
            total={data?.total_steps ?? 1}
          />
        </div>

        <div className="mt-5 grid grid-cols-4 gap-2">
          <Stat
            icon={<Mail className="h-3.5 w-3.5" />}
            label="sent"
            value={data?.stats?.emails_sent ?? 0}
          />
          <Stat
            icon={<MailOpen className="h-3.5 w-3.5" />}
            label="opened"
            value={data?.stats?.emails_opened ?? 0}
            highlight={
              (data?.stats?.emails_opened ?? 0) > 0 ? "emerald" : undefined
            }
          />
          <Stat
            icon={<MousePointerClick className="h-3.5 w-3.5" />}
            label="clicked"
            value={data?.stats?.emails_clicked ?? 0}
          />
          <Stat
            icon={<MessageSquare className="h-3.5 w-3.5" />}
            label="replied"
            value={data?.stats?.emails_replied ?? 0}
            highlight={
              (data?.stats?.emails_replied ?? 0) > 0
                ? "emerald-bold"
                : undefined
            }
          />
        </div>

        <div className="mt-5 max-h-[260px] space-y-2 overflow-y-auto pr-1">
          {(data?.events ?? []).slice(0, 50).map((event, i) => {
            const kind = normalizeEventKind(event.type);
            // Migrated leads ship the body via metadata when Lemlist's
            // feed didn't — resolveEmailBody picks the right key based
            // on step. Click-through is gated on having *some* body
            // (live or fallback), so opened/clicked/bounced rows stay
            // non-interactive but a sent row with only metadata content
            // becomes openable.
            const hasBody = !!resolveEmailBody(event, company);
            const clickable =
              (kind === "sent" || kind === "replied") && hasBody;
            return (
              <EventRow
                key={`${event.type}-${event.occurred_at}-${i}`}
                event={event}
                hasBody={hasBody}
                onPick={
                  clickable ? () => setSelectedEvent(event) : undefined
                }
              />
            );
          })}
          {(data?.events ?? []).length === 0 && (
            <p className="text-xs text-slate-400">No events yet.</p>
          )}
        </div>

        {(data?.contact_email || data?.contact_name) && (
          <ContactCard
            contact={
              company.contacts.find(
                (c) => !!data?.contact_email && c.email === data.contact_email,
              ) ?? null
            }
            fallbackName={data?.contact_name ?? data?.contact_email ?? ""}
            fallbackEmail={data?.contact_email ?? ""}
          />
        )}
      </Card>
      <EnrollDialog
        open={enrollOpen}
        onOpenChange={setEnrollOpen}
        projectSlug={projectSlug}
        company={company}
        onEnrolled={handleEnrolled}
      />
      <RemoveFromCampaignDialog
        open={removeConfirmOpen}
        onOpenChange={(o) => {
          if (!removing) setRemoveConfirmOpen(o);
        }}
        busy={removing}
        previousTeam={!!data?.previous_team}
        onConfirm={confirmRemove}
      />
      <EmailDetailSheet
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        subject={resolveEmailSubject(selectedEvent, company)}
        body={resolveEmailBody(selectedEvent, company)}
        bodyType={resolveEmailBodyType(selectedEvent, company)}
        direction={
          selectedEvent
            ? normalizeEventKind(selectedEvent.type) === "replied"
              ? "inbound"
              : "outbound"
            : null
        }
        // For inbound replies, the prospect is the sender; for sent
        // events, the prospect is the recipient (data.contact_*).
        contactEmail={
          selectedEvent &&
          normalizeEventKind(selectedEvent.type) === "replied"
            ? selectedEvent.sender_email ?? data?.contact_email ?? null
            : data?.contact_email ?? null
        }
        contactName={
          selectedEvent &&
          normalizeEventKind(selectedEvent.type) === "replied"
            ? selectedEvent.sender_name ?? data?.contact_name ?? null
            : data?.contact_name ?? null
        }
        sentAt={selectedEvent?.occurred_at ?? null}
        campaignName={data?.campaign_name ?? null}
        step={selectedEvent?.step ?? null}
        status={
          selectedEvent
            ? normalizeEventKind(selectedEvent.type) === "replied"
              ? "replied"
              : "sent"
            : null
        }
        lemlistUrl={lemlistLeadUrl}
      />
    </>
  );
}

function RemoveFromCampaignDialog({
  open,
  onOpenChange,
  busy,
  previousTeam,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  busy: boolean;
  /** A previous Lemlist team's lead: removed in Prism only. */
  previousTeam: boolean;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {previousTeam
              ? "Remove this company from its previous-team campaign?"
              : "Remove this lead from the Lemlist campaign?"}
          </DialogTitle>
          <DialogDescription>
            {previousTeam
              ? "In Prism only: Lemlist is not called, and the lead stays as it is in the previous Lemlist team. The company keeps its email history and status, and its contacts can then join an Email campaign."
              : "The email sequence will be permanently stopped. The contact will be re-subscribable for future campaigns."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="bg-rose-600 text-white hover:bg-rose-700 focus-visible:ring-rose-300"
          >
            {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            {busy ? "Removing…" : previousTeam ? "Remove in Prism" : "Remove"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section className="rounded-plane bg-white p-5 shadow-plane">
      {children}
    </section>
  );
}

function SequenceHeader({
  isPaused,
  previousTeam,
  campaignName,
  canEdit,
  canResume,
  pausing,
  resuming,
  onPause,
  onResume,
  onAskRemove,
  lemlistLeadUrl,
}: {
  isPaused: boolean;
  /** A previous Lemlist team's lead: history, which can only be removed in Prism. */
  previousTeam: boolean;
  campaignName: string | null;
  canEdit: boolean;
  canResume: boolean;
  pausing: boolean;
  resuming: boolean;
  onPause: () => void;
  onResume: () => void;
  onAskRemove: () => void;
  lemlistLeadUrl: string | null;
}) {
  const colors = statusColors(previousTeam ? "completed" : isPaused ? "paused" : "active");
  const showMenu = canEdit || !!lemlistLeadUrl;
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-slate-900">
            Email sequence
          </h3>
          <LegacyTag />
          {/* transition-colors crossfades emerald ↔ amber on the badge when
           *  isPaused flips, without remounting the element. */}
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors duration-150",
              colors.bg,
              colors.text,
              colors.border,
            )}
          >
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full transition-colors duration-150",
                colors.dot,
                isPaused && !previousTeam && "animate-pulse",
              )}
            />
            {previousTeam ? "Previous team" : isPaused ? "Paused" : "Active"}
          </span>
        </div>
        {campaignName && (
          <p className="mt-1 truncate text-xs text-slate-500">
            Campaign: {campaignName}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {canEdit && !previousTeam && (
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={isPaused ? "paused" : "active"}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {isPaused ? (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          type="button"
                          size="sm"
                          disabled={resuming || !canResume}
                          onClick={onResume}
                          className="h-8 bg-emerald-600 text-xs text-white hover:bg-emerald-700 focus-visible:ring-emerald-300"
                        >
                          {resuming ? (
                            <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                          ) : (
                            <Play className="mr-1.5 h-3 w-3" />
                          )}
                          Resume sequence
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!canResume && (
                      <TooltipContent side="top" className="text-xs">
                        The original contact email is no longer on this
                        company, add the contact back to resume.
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pausing}
                  onClick={onPause}
                  className="h-8 border-amber-200 bg-amber-50/40 text-xs text-amber-700 hover:bg-amber-50"
                >
                  {pausing ? (
                    <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                  ) : (
                    <Pause className="mr-1.5 h-3 w-3" />
                  )}
                  Pause sequence
                </Button>
              )}
            </motion.div>
          </AnimatePresence>
        )}
        {showMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Sequence actions"
                className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              {lemlistLeadUrl && (
                <DropdownMenuItem
                  onSelect={() =>
                    window.open(lemlistLeadUrl, "_blank", "noopener,noreferrer")
                  }
                >
                  <ExternalLink className="mr-2 h-3.5 w-3.5" />
                  View in Lemlist
                </DropdownMenuItem>
              )}
              {canEdit && lemlistLeadUrl && <DropdownMenuSeparator />}
              {canEdit && (
                <DropdownMenuItem
                  onSelect={onAskRemove}
                  className="text-rose-600 focus:bg-rose-50 focus:text-rose-700"
                >
                  <Trash2 className="mr-2 h-3.5 w-3.5" />
                  Remove from campaign
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

function SequenceProgress({
  current,
  total,
}: {
  current: number;
  total: number;
}) {
  const safeTotal = Math.max(total, 1);
  const safeCurrent = Math.min(Math.max(current, 1), safeTotal);
  const steps = Array.from({ length: safeTotal }, (_, i) => i + 1);
  return (
    <div>
      <div className="mb-2 flex justify-between text-[11px] font-medium text-slate-500">
        {steps.map((s) => (
          <span
            key={s}
            className={cn(
              "flex items-center gap-1",
              s < safeCurrent && "text-indigo-600",
              s === safeCurrent && "text-indigo-700",
            )}
          >
            Step {s}
            {s < safeCurrent && <Check className="h-3 w-3" />}
          </span>
        ))}
      </div>
      <div className="relative flex items-center">
        {steps.map((s, i) => {
          const completed = s < safeCurrent;
          const active = s === safeCurrent;
          return (
            <div key={s} className="flex flex-1 items-center last:flex-none">
              <span
                className={cn(
                  "flex h-3 w-3 shrink-0 items-center justify-center rounded-full",
                  completed && "bg-indigo-600",
                  active && "bg-indigo-600 ring-2 ring-indigo-200",
                  !completed && !active && "border border-slate-300 bg-white",
                )}
                aria-hidden
              >
                {completed && <Check className="h-2 w-2 text-white" />}
              </span>
              {i < steps.length - 1 && (
                <span
                  className={cn(
                    "mx-1 h-0.5 flex-1 rounded-full",
                    s < safeCurrent ? "bg-indigo-500" : "bg-slate-200",
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  highlight?: "emerald" | "emerald-bold";
}) {
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50/50 px-2 py-1.5">
      <span
        className={cn(
          "text-slate-400",
          highlight === "emerald" && "text-emerald-500",
          highlight === "emerald-bold" && "text-emerald-600",
        )}
      >
        {icon}
      </span>
      <span
        className={cn(
          "text-xs",
          highlight === "emerald" && "font-medium text-emerald-700",
          highlight === "emerald-bold" && "font-semibold text-emerald-700",
        )}
      >
        {value} {label}
      </span>
    </div>
  );
}

function EventRow({
  event,
  hasBody = false,
  onPick,
}: {
  event: OutreachEvent;
  hasBody?: boolean;
  onPick?: () => void;
}) {
  const kind = normalizeEventKind(event.type);
  const colors = eventColors(kind);
  // Lemlist's manual-reply events come through with "manual" in the
  // type (e.g. emailsManual / manualSent / manualReplied). Detect it
  // so we can flag the row with a small "Manual" badge — it's the only
  // way to tell a hand-written reply apart from an automatic sequence
  // email.
  const isManual = /manual/i.test(event.type);
  const inner = (
    <>
      <span
        className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", colors.dot)}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="inline-flex items-center gap-1 text-[13px] font-medium text-slate-800 capitalize">
            {labelForEvent(event, kind)}
            {isManual && (
              <span className="rounded bg-slate-100 px-1 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-500">
                Manual
              </span>
            )}
            {hasBody && (
              <FileText
                className="h-3 w-3 text-slate-400"
                aria-label="Email content available"
              />
            )}
          </span>
          <span className="shrink-0 text-[11px] text-slate-400">
            {formatDateTime(event.occurred_at, "MMM d, h:mm a")}
          </span>
        </div>
        {event.subject && (
          <p className="truncate text-xs italic text-slate-500">
            {event.subject}
          </p>
        )}
        {kind === "replied" && (
          <p className="text-[11px] font-semibold text-emerald-600">
            <Star className="mr-1 inline h-3 w-3 fill-emerald-500 text-emerald-500" />
            Replied
          </p>
        )}
        {kind === "bounced" && (
          <p className="text-[11px] font-medium text-rose-600">
            <AlertCircle className="mr-1 inline h-3 w-3" />
            Bounced
          </p>
        )}
      </div>
    </>
  );
  if (onPick) {
    return (
      <button
        type="button"
        onClick={onPick}
        className="flex w-full items-start gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors duration-150 hover:bg-indigo-500/[0.04]"
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="flex items-start gap-2.5 rounded-lg px-2 py-1.5">{inner}</div>
  );
}

function ContactCard({
  contact,
  fallbackName,
  fallbackEmail,
}: {
  contact: EmbeddedContact | null;
  fallbackName: string;
  fallbackEmail: string;
}) {
  const name = contact?.full_name ?? fallbackName;
  const email = contact?.email ?? fallbackEmail;
  return (
    <div className="mt-5 flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50/40 px-3 py-2">
      <Avatar className="h-8 w-8 border border-slate-200">
        <AvatarFallback className="bg-indigo-100 text-[10px] font-semibold text-indigo-700">
          {initials(name)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-slate-900">{name}</div>
        <div className="truncate text-xs text-slate-500">{email}</div>
      </div>
    </div>
  );
}

type EventKind = "sent" | "opened" | "clicked" | "replied" | "bounced" | "other";

/**
 * Lemlist event types are spelled `emailsSent` / `emailsOpened` / …; legacy
 * shapes used `sent` / `opened` / …. Map both to a shared kind for rendering.
 */
function normalizeEventKind(rawType: string): EventKind {
  const t = rawType.toLowerCase();
  if (t.includes("sent")) return "sent";
  if (t.includes("opened")) return "opened";
  if (t.includes("clicked")) return "clicked";
  if (t.includes("replied")) return "replied";
  if (t.includes("bounced")) return "bounced";
  return "other";
}

function labelForEvent(event: OutreachEvent, kind: EventKind): string {
  const verb = kind === "other" ? event.type : kind;
  if (event.step) return `Email ${event.step} ${verb}`;
  return `Email ${verb}`;
}

function statusColors(status: string) {
  switch (status) {
    case "active":
      return {
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        border: "border-emerald-200",
        dot: "bg-emerald-500",
      };
    case "paused":
      return {
        bg: "bg-amber-50",
        text: "text-amber-700",
        border: "border-amber-200",
        dot: "bg-amber-500",
      };
    case "completed":
      return {
        bg: "bg-slate-100",
        text: "text-slate-600",
        border: "border-slate-200",
        dot: "bg-slate-400",
      };
    case "bounced":
      return {
        bg: "bg-rose-50",
        text: "text-rose-700",
        border: "border-rose-200",
        dot: "bg-rose-500",
      };
    default:
      return {
        bg: "bg-slate-100",
        text: "text-slate-600",
        border: "border-slate-200",
        dot: "bg-slate-400",
      };
  }
}

function eventColors(kind: EventKind) {
  switch (kind) {
    case "sent":
      return { dot: "bg-blue-500" };
    case "opened":
      return { dot: "bg-emerald-500" };
    case "clicked":
      return { dot: "bg-violet-500" };
    case "replied":
      return { dot: "bg-indigo-600" };
    case "bounced":
      return { dot: "bg-rose-500" };
    default:
      return { dot: "bg-slate-400" };
  }
}

function readMetaString(
  meta: Record<string, unknown>,
  key: string,
): string | null {
  const v = meta[key];
  return typeof v === "string" && v ? v : null;
}

/** Pick the metadata body key for a Lemlist event when the API
 *  didn't ship one (typical for migrated M&A leads — Lemlist's
 *  activity feed has the timestamp but not the content). Step 1
 *  maps to `generated_email_body`, step 2 to
 *  `generated_followup1_body`. Inbound replies aren't in metadata
 *  so we return null and let the sheet show its "not available"
 *  fallback. */
function fallbackBodyForEvent(
  event: OutreachEvent,
  meta: Record<string, unknown>,
): string | null {
  if (normalizeEventKind(event.type) === "replied") return null;
  const step = event.step ?? 1;
  if (step === 1) return readMetaString(meta, "generated_email_body");
  if (step === 2) return readMetaString(meta, "generated_followup1_body");
  return null;
}

function resolveEmailBody(
  event: OutreachEvent | null,
  company: CompanyDetailType,
): string | null {
  if (!event) return null;
  if (event.body) return event.body;
  const meta = (company.metadata ?? {}) as Record<string, unknown>;
  return fallbackBodyForEvent(event, meta);
}

function resolveEmailSubject(
  event: OutreachEvent | null,
  company: CompanyDetailType,
): string | null {
  if (!event) return null;
  if (event.subject) return event.subject;
  // Fall back to the pre-generated subject for outbound migrated
  // events. Replies don't have a stable subject in metadata.
  if (normalizeEventKind(event.type) === "replied") return null;
  const meta = (company.metadata ?? {}) as Record<string, unknown>;
  return readMetaString(meta, "generated_email_subject");
}

function resolveEmailBodyType(
  event: OutreachEvent | null,
  company: CompanyDetailType,
): OutreachBodyType | null {
  if (!event) return null;
  // Live body kept whatever type the Lemlist API returned.
  if (event.body) return event.body_type ?? null;
  // Generated bodies are plain text from the icebreaker template.
  return fallbackBodyForEvent(event, (company.metadata ?? {}) as Record<string, unknown>)
    ? "text"
    : null;
}

/** Minimal Lemlist card rendered for companies whose pipeline_status
 *  is in the email_* family but whose live outreach status came back
 *  empty — typically the 374 leads migrated without a campaign id.
 *  Renders the metadata snapshot the migration carried over so the
 *  page reflects the truth (the lead IS in an email flow) instead of
 *  showing the misleading "No email sequence active" empty state. */
function MigratedSequenceCard({
  pipelineStatus,
  meta,
  lemlistLeadUrl,
}: {
  pipelineStatus: string;
  meta: Record<string, unknown>;
  lemlistLeadUrl: string | null;
}) {
  const sentAt = readMetaString(meta, "email_sent_at");
  const openedAt = readMetaString(meta, "email_opened_at");
  const clickedAt = readMetaString(meta, "email_clicked_at");
  const repliedAt = readMetaString(meta, "email_replied_at");
  const bounced = meta.email_bounced === true;
  const sentCount =
    typeof meta.emails_sent_count === "number"
      ? meta.emails_sent_count
      : null;
  const campaignId = readMetaString(meta, "lemlist_campaign_id");
  // Pre-generated email content (M&A flow). The Lemlist API exposes
  // the body live for CFO sequences via the EventRow → EmailDetailSheet
  // path; for migrated M&A leads the body is captured at generation
  // time and stored on the company. Subject is optional — when missing
  // we still surface the body using a generic "Email content" label.
  const emailSubject = readMetaString(meta, "generated_email_subject");
  const emailBody = readMetaString(meta, "generated_email_body");
  const followUp1Body = readMetaString(meta, "generated_followup1_body");
  const hasAnyEvent =
    !!sentAt || !!openedAt || !!clickedAt || !!repliedAt || bounced;
  // A previous Lemlist team's lead removed in Prism: its provenance says when.
  const legacy =
    meta.lemlist_legacy && typeof meta.lemlist_legacy === "object"
      ? (meta.lemlist_legacy as Record<string, unknown>)
      : null;
  const removedAt = legacy ? readMetaString(legacy, "removed_at") : null;
  const campaignName = readMetaString(meta, "lemlist_campaign_name");

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-slate-900">
              Email sequence
            </h3>
            <LegacyTag />
          </div>
          <p className="mt-0.5 text-[11px] text-slate-400">
            From metadata snapshot, no live campaign data
          </p>
        </div>
        <PipelineStatusBadge status={pipelineStatus} />
      </div>

      {removedAt && (
        <p className="text-xs text-slate-500">
          Removed from {campaignName ?? "its campaign"} in Prism on{" "}
          {formatDateTime(removedAt, "MMM d, yyyy")}. The lead stays in the previous Lemlist team.
        </p>
      )}

      <div className="space-y-2">
        {sentAt && (
          <>
            <TimelineRow
              icon={<Send className="h-3.5 w-3.5 text-indigo-500" />}
              label="Sent"
              at={sentAt}
              tone="default"
            />
            {emailBody && (
              <EmailBodyDisclosure
                summary={emailSubject ?? "Email content"}
                body={emailBody}
              />
            )}
          </>
        )}
        {sentCount != null && sentCount > 1 && (
          <>
            <div className="flex items-center gap-2 text-xs">
              <Mail className="h-3.5 w-3.5 text-indigo-400" />
              <span className="text-slate-500">
                {sentCount} emails sent total
              </span>
            </div>
            {followUp1Body && (
              <EmailBodyDisclosure summary="Follow-up 1" body={followUp1Body} />
            )}
          </>
        )}
        {openedAt && (
          <TimelineRow
            icon={<MailOpen className="h-3.5 w-3.5 text-emerald-500" />}
            label="Opened"
            at={openedAt}
            tone="emerald"
          />
        )}
        {clickedAt && (
          <TimelineRow
            icon={<MousePointerClick className="h-3.5 w-3.5 text-blue-500" />}
            label="Clicked"
            at={clickedAt}
            tone="blue"
          />
        )}
        {repliedAt && (
          <TimelineRow
            icon={<MessageSquare className="h-3.5 w-3.5 text-emerald-600" />}
            label="Replied"
            at={repliedAt}
            tone="emerald-bold"
          />
        )}
        {bounced && (
          <div className="flex items-center gap-2 text-xs">
            <AlertCircle className="h-3.5 w-3.5 text-rose-500" />
            <span className="text-rose-600">Bounced</span>
          </div>
        )}
        {!hasAnyEvent && (
          <p className="text-xs text-slate-400">
            Pending, email not sent yet
          </p>
        )}
      </div>

      {(campaignId || lemlistLeadUrl) && (
        <div className="flex items-center justify-between border-t border-slate-900/[0.06] pt-2 text-[10px] text-slate-400">
          {campaignId ? (
            <span className="truncate">Campaign: {campaignId}</span>
          ) : (
            <span />
          )}
          {lemlistLeadUrl && (
            <a
              href={lemlistLeadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-slate-500 hover:text-indigo-600"
            >
              <ExternalLink className="h-3 w-3" />
              View in Lemlist
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function EmailBodyDisclosure({
  summary,
  body,
}: {
  summary: string;
  body: string;
}) {
  return (
    <details className="ml-5 text-xs">
      <summary className="cursor-pointer truncate text-indigo-500 hover:text-indigo-700">
        {summary}
      </summary>
      <div className="mt-2 whitespace-pre-wrap rounded-lg border border-slate-900/[0.06] bg-slate-50 p-3 text-slate-600">
        {body}
      </div>
    </details>
  );
}

function TimelineRow({
  icon,
  label,
  at,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  at: string;
  tone: "default" | "emerald" | "blue" | "emerald-bold";
}) {
  const labelClass =
    tone === "emerald"
      ? "text-emerald-600"
      : tone === "blue"
        ? "text-blue-600"
        : tone === "emerald-bold"
          ? "font-medium text-emerald-700"
          : "text-slate-600";
  return (
    <div className="flex items-center gap-2 text-xs">
      {icon}
      <span className={labelClass}>{label}</span>
      <span className="ml-auto text-slate-400">
        {formatDateTime(at, "MMM d, h:mm a")}
      </span>
    </div>
  );
}

function PipelineStatusBadge({ status }: { status: string }) {
  const label = status
    .replace(/^email_/, "")
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
  const tone = badgeTone(status);
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
        tone.bg,
        tone.text,
        tone.border,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", tone.dot)} />
      {label}
    </span>
  );
}

function badgeTone(status: string) {
  if (status.includes("replied"))
    return {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      border: "border-emerald-200",
      dot: "bg-emerald-500",
    };
  if (status.includes("bounced") || status.includes("refused"))
    return {
      bg: "bg-rose-50",
      text: "text-rose-700",
      border: "border-rose-200",
      dot: "bg-rose-500",
    };
  if (status.includes("opened") || status.includes("clicked"))
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      border: "border-blue-200",
      dot: "bg-blue-500",
    };
  return {
    bg: "bg-indigo-50",
    text: "text-indigo-700",
    border: "border-indigo-200",
    dot: "bg-indigo-500",
  };
}

function extractError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Action failed.";
}

/** This panel is the legacy workflow: one Lemlist lead per company, outside
 *  the Email module — whose campaigns have their own card. A company is
 *  emailed one way only. */
function LegacyTag() {
  return (
    <span
      title="Legacy workflow: one Lemlist lead per company, outside the Email module. A company is emailed one way only."
      className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500"
    >
      Legacy
    </span>
  );
}
